import { isString, trim } from 'lodash-es'
import { forwardRef, useCallback } from 'react'

import { type TextFieldProps as MuiTextFieldProps } from '@mui/material'
import { withReadOnly } from './helpers/withReadOnly/withReadOnly'
import * as $ from './TextField.styled'
import type { TextFieldStyledProps } from './TextField.styled'

export interface TextFieldProps extends Omit<MuiTextFieldProps, 'error'>, TextFieldStyledProps {
  'data-testid'?: string
  error?: string | boolean
}

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      id,
      warning,
      children,
      size = 'medium',
      error,
      helperText,
      value,
      readOnly,
      slotProps,
      onBlur,
      onInput,
      onChange,
      ...props
    },
    ref,
  ) => {
    const handleBlur: NonNullable<TextFieldProps['onBlur']> = useCallback(
      (e) => {
        if (readOnly) {
          onBlur?.(e)

          return
        }

        const trimmedValue = trim(e.target.value)
        const event = { ...e, target: { ...e.target, value: trimmedValue } }

        onBlur?.(event)

        if (value !== trimmedValue) {
          onChange?.(event)
        }
      },
      [onBlur, onChange, value, readOnly],
    )

    const handleInput: NonNullable<TextFieldProps['onInput']> = (e) => {
      const nativeEvent = e.nativeEvent as InputEvent
      const target = e.target as HTMLInputElement

      if (nativeEvent.inputType === 'insertFromPaste' && nativeEvent.data) {
        target.value = target.value.replace(nativeEvent.data, trim(nativeEvent.data))
      }

      onInput?.(e)
    }

    // A read-only field has nothing to type into, so it shouldn't wake up when clicked. MUI derives
    // the floating label, the outline notch and the focus ring from this one flag, so holding it
    // down leaves the label resting inside the field. DOM focus still happens — the theme draws the
    // read-only focus outline off the focused value element instead.
    const focusedState = readOnly ? { focused: false } : undefined

    return (
      <$.TextField
        id={id}
        data-testid={id}
        size={size}
        value={value}
        error={Boolean(error)}
        helperText={isString(error) ? error : helperText}
        readOnly={readOnly}
        slotProps={withReadOnly({ readOnly, slotProps, InputProps: props.InputProps })}
        {...focusedState}
        {...(warning && { 'data-warning': true, warning })}
        {...props}
        onInput={handleInput}
        onChange={onChange}
        onBlur={handleBlur}
        ref={ref}
      >
        {children}
      </$.TextField>
    )
  },
)

TextField.displayName = 'TextField'
